import { useEffect, useRef } from "react";
import { useAuth } from "./useAuth";
import Intercom, { boot, shutdown } from "@intercom/messenger-js-sdk";
import { useConsentStore } from "@/lib/consent/consent-store";

const INTERCOM_APP_ID = "ll0leu0j";

type IntercomSettings = {
  app_id: string;
  user_id?: string;
  name?: string;
  email?: string;
  created_at?: number;
  user_hash?: string;
};

/** "idle" — nigdy nie wystartował, "running" — działa, "off" — wygaszony po cofnięciu zgody. */
type MessengerStatus = "idle" | "running" | "off";

export function useIntercom() {
  const { user, isAuthenticated } = useAuth();
  const functionalConsent = useConsentStore((state) => state.functional);
  const statusRef = useRef<MessengerStatus>("idle");

  useEffect(() => {
    // VP-312: czat to kategoria funkcjonalna. Bez zgody messenger nie startuje,
    // a po jej wycofaniu gasimy już uruchomioną sesję.
    if (!functionalConsent) {
      if (statusRef.current === "running") {
        shutdown();
        statusRef.current = "off";
      }
      return;
    }

    const settings: IntercomSettings =
      isAuthenticated && user
        ? {
            app_id: INTERCOM_APP_ID,
            user_id: String(user.id),
            name: user.username || "User",
            email: user.email,
            created_at: user.createdAt
              ? Math.floor(new Date(user.createdAt).getTime() / 1000)
              : undefined,
            user_hash: user.intercomHash || undefined,
          }
        : { app_id: INTERCOM_APP_ID };

    // Po `shutdown()` messenger wstaje wyłącznie przez `boot` — ponowne wywołanie
    // Intercom() robi tylko `update` i czat zostałby martwy do końca sesji.
    if (statusRef.current === "off") {
      boot(settings);
    } else {
      Intercom(settings);
    }
    statusRef.current = "running";
  }, [isAuthenticated, user, functionalConsent]);
}
